import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";

import { formatMoney } from "../../../../../shared/utils/formatMoney";
import {
  PRODUCTION_STATUS,
  PRODUCTION_STATUS_LABELS,
} from "../utils/productionModel";
import {
  formatProductionDate,
  formatProductionQuantity,
} from "../utils/productionFormatters";

const columns = [
  { key: "date", label: "Fecha", width: "minmax(110px, 0.9fr)" },
  { key: "product", label: "Elaborado", width: "minmax(210px, 1.8fr)" },
  { key: "quantity", label: "Cantidad producida", width: "minmax(155px, 1.2fr)" },
  { key: "cost", label: "Costo total", width: "minmax(130px, 1fr)" },
  { key: "producer", label: "Producido por", width: "minmax(170px, 1.35fr)" },
  { key: "status", label: "Estado", width: "minmax(105px, 0.85fr)" },
  { key: "actions", label: "Acciones", width: "minmax(100px, 0.8fr)" },
];

const gridSx = {
  display: "grid",
  gridTemplateColumns: columns.map((column) => column.width).join(" "),
  columnGap: 0.75,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

function TextCell({ children }) {
  return (
    <Typography
      noWrap
      title={typeof children === "string" ? children : undefined}
      sx={{
        color: "primary.900",
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}

function StatusPill({ status }) {
  const produced = status === PRODUCTION_STATUS.PRODUCED;
  return (
    <Box
      sx={{
        mx: "auto",
        width: 82,
        px: 0.8,
        py: 0.55,
        borderRadius: "20px",
        bgcolor: produced
          ? "rgba(33, 150, 83, 0.16)"
          : "rgba(195, 60, 60, 0.16)",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
      }}
    >
      <Typography
        sx={{
          color: produced ? "rgba(18, 111, 63, 1)" : "rgba(160, 49, 49, 1)",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {PRODUCTION_STATUS_LABELS[status] || status || "—"}
      </Typography>
    </Box>
  );
}

function ActionButton({ label, title, disabled, onClick, children }) {
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          sx={{
            width: 31,
            height: 31,
            bgcolor: "neutral.50",
            boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
            "&:hover": { bgcolor: "secondary.100" },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function ProductionsTable({
  rows = [],
  loading = false,
  onView,
  onCancel,
  emptyMessage = "Todavía no hay producciones registradas.",
}) {
  return (
    <Box sx={{ flex: 1, minHeight: 0, borderRadius: "20px", overflow: "hidden" }}>
      <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box
          sx={{
            minWidth: 1080,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              bgcolor: "neutral.600",
              flexShrink: 0,
              overflowY: "hidden",
              scrollbarGutter: "stable",
              px: 2,
              py: 1.6,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "logoblanco",
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              bgcolor: "neutral.50",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              scrollbarGutter: "stable",
            }}
          >
            {rows.map((production) => {
              const name = production.preparedProduct?.name || "—";
              const canCancel = production.status === PRODUCTION_STATUS.PRODUCED;
              return (
                <Box
                  key={production.id}
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderBottom: "1px solid rgba(0,0,0,.06)",
                    minHeight: 58,
                    "&:hover": { bgcolor: "rgba(0,0,0,.03)" },
                  }}
                >
                  <Box sx={gridSx}>
                    <TextCell>{formatProductionDate(production.producedAt)}</TextCell>
                    <TextCell>{name}</TextCell>
                    <TextCell>
                      {formatProductionQuantity(
                        production.producedQuantity,
                        production.preparedProduct?.resultUnit
                      )}
                    </TextCell>
                    <TextCell>{formatMoney(production.totalCost)}</TextCell>
                    <TextCell>{production.producedBy || "—"}</TextCell>
                    <StatusPill status={production.status} />
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "center" }}>
                      <ActionButton
                        title="Ver detalle"
                        label={`Ver detalle de ${name}`}
                        disabled={loading || !onView}
                        onClick={() => onView?.(production)}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "primary.700" }} />
                      </ActionButton>
                      {canCancel ? (
                        <ActionButton
                          title="Anular producción"
                          label={`Anular producción de ${name}`}
                          disabled={loading || !onCancel}
                          onClick={() => onCancel?.(production)}
                        >
                          <DeleteOutlineIcon
                            sx={{ fontSize: 18, color: "secondary.700" }}
                          />
                        </ActionButton>
                      ) : null}
                    </Stack>
                  </Box>
                </Box>
              );
            })}

            {rows.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "neutral.700", fontWeight: 600 }}>
                  {loading ? "Cargando producciones..." : emptyMessage}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
